import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Card, Button, Checkbox, Title, TextInput } from "@yoast/ui-library";
import { defineMessages, FormattedMessage } from "react-intl";
import { prepareInternalRequest, doRequest } from "shared-frontend/functions/api";
import { SUBSCRIBE_NEWSLETTER_SUCCESS } from "../../config/Analytics";
import yoastIcon from "../../icons/yoast-icon.svg";
import * as styles from "./styles.module.scss";

const messages = defineMessages( {
	title: {
		id: "newsletterNotification.title",
		defaultMessage: "You're missing out on our newest features and our best offers!",
	},
	emailLabel: {
		id: "newsletterNotification.emailLabel",
		defaultMessage: "Email address",
	},
	consent: {
		id: "newsletterNotification.consent",
		defaultMessage: "I agree to receive the Yoast newsletter and marketing emails.",
	},
	consentDetails: {
		id: "newsletterNotification.consentDetails",
		defaultMessage: "I have read and agree to the {detailsLink}.",
	},
	details: {
		id: "newsletterNotification.details",
		defaultMessage: "Privacy Policy",
	},
	signMeUp: {
		id: "newsletterNotification.signMeUp",
		defaultMessage: "Yes, sign me up!",
	},
	dismiss: {
		id: "newsletterNotification.dismiss",
		defaultMessage: "Dismiss",
	},
} );

/**
 * Newsletter notification banner component.
 *
 * Prompts users to subscribe to the Yoast newsletter.
 * Requires consent checkbox before enabling the submit button.
 *
 * @param {Object} props Component props.
 * @param {string} props.email The user's email address.
 * @param {Function} props.onDismiss Callback when the notification is dismissed.
 *
 * @returns {JSX.Element} The newsletter notification banner.
 */
const NewsletterNotification = ( { email, onDismiss } ) => {
	const [ emailValue, setEmailValue ] = useState( email );
	const [ isConsented, setIsConsented ] = useState( false );
	const [ isSubmitting, setIsSubmitting ] = useState( false );
	const dispatch = useDispatch();

	const handleSubmit = useCallback( async() => {
		setIsSubmitting( true );
		try {
			const isOriginalEmail = emailValue.trim().toLowerCase() === email.trim().toLowerCase();
			let request;

			if ( isOriginalEmail ) {
				// Same email as the authenticated user — no double opt-in needed.
				request = prepareInternalRequest(
					"Mailing-list/subscribeToNewsletter",
					"POST",
					{},
				);
			} else {
				// Different email — use the public subscribe endpoint with double opt-in.
				request = prepareInternalRequest(
					"Mailing-list/subscribe",
					"POST",
					{
						customerDetails: { email: emailValue.trim() },
						list: "Yoast newsletter",
					},
				);
			}

			await doRequest( request );
			dispatch( { type: SUBSCRIBE_NEWSLETTER_SUCCESS, status: "subscribed" } );
			onDismiss();
		} catch {
			// On error, still dismiss to avoid blocking the user.
			onDismiss();
		}
	}, [ dispatch, onDismiss, emailValue, email ] );

	const detailsLink = (
		<a
			className={ styles.privacyLink }
			href="https://www.newfold.com/privacy-center/privacy"
			target="_blank"
			rel="noopener noreferrer"
		>
			<FormattedMessage { ...messages.details } />
		</a>
	);

	return (
		<Card className={ styles.newsletterNotificationCard }>
			<button
				type="button"
				className={ styles.closeButton }
				onClick={ onDismiss }
				aria-label="Close"
			>
				<XMarkIcon aria-hidden="true" />
			</button>
			<div className={ styles.container }>
				<div>
					<img src={ yoastIcon } alt="" className={ styles.icon } />
				</div>
				<div className={ styles.content }>
					<Title size={ "5" }>
						<FormattedMessage { ...messages.title } />
					</Title>
					<div className={ styles.emailField }>
						<label htmlFor="newsletter-email" className={ styles.emailLabel }>
							<FormattedMessage { ...messages.emailLabel } />
						</label>
						<TextInput
							id="newsletter-email"
							data-testid="newsletter-email"
							value={ emailValue }
							onChange={ ( e ) => setEmailValue( e.target.value ) }
						/>
					</div>
					<div className={ styles.consentLabel }>
						<Checkbox
							id="newsletter-consent"
							name="newsletter-consent"
							value="newsletter-consent"
							checked={ isConsented }
							onChange={ () => setIsConsented( prev => ! prev ) }
							className="yst-mt-[2px]"
						/>
						<label htmlFor="newsletter-consent">
							<FormattedMessage { ...messages.consent } />
							<br />
							<FormattedMessage
								{ ...messages.consentDetails }
								values={ { detailsLink } }
							/>
						</label>
					</div>
					<div className={ styles.buttonContainer }>
						<Button
							variant="primary"
							onClick={ handleSubmit }
							disabled={ ! isConsented || isSubmitting || ! emailValue.trim() || ! emailValue.includes( "@" ) }
						>
							<FormattedMessage { ...messages.signMeUp } />
						</Button>
						<Button
							variant="tertiary"
							onClick={ onDismiss }
						>
							<FormattedMessage { ...messages.dismiss } />
						</Button>
					</div>
				</div>
			</div>
		</Card>
	);
};

NewsletterNotification.propTypes = {
	email: PropTypes.string.isRequired,
	onDismiss: PropTypes.func.isRequired,
};

export default NewsletterNotification;
